import { useState } from "react";
import PageHero from "../components/PageHero";

interface Model {
  id: number;
  slug?: string;
  name: string;
  type: string;
  bedrooms: string;
  area: string;
  price: string;
  roi: string;
  style: string;
  image: string;
  desc: string;
}

const MODELS: Model[] = [
  {
    id: 1,
    slug: "nomad-one",
    name: "Nomad One",
    type: "Compact Cabin",
    bedrooms: "1 Bed",
    area: "360 sq ft",
    price: "From ₹52 L",
    roi: "26%",
    style: "Airbnb",
    desc: "A lightweight single-cabin A-frame for budget-first investors, ideal for short-stay Airbnb rentals.",
    image: "https://readdy.ai/api/search-image?query=Small%20compact%20wooden%20A-frame%20cabin%20with%20triangular%20roof%20in%20a%20green%20hill%20clearing%2C%20bright%20daylight%2C%20minimalist%20architectural%20render%2C%20warm%20natural%20tones%2C%20high%20detail&width=800&height=600&seq=list-01&orientation=landscape",
  },
  {
    id: 2,
    slug: "aurora",
    name: "Aurora",
    type: "Studio A-Frame",
    bedrooms: "1 Bed",
    area: "420 sq ft",
    price: "From ₹66 L",
    roi: "28%",
    style: "Staycation",
    desc: "Our signature glass-front studio with a viewing deck — the best seller for scenic staycation spots.",
    image: "https://readdy.ai/api/search-image?query=Glass%20fronted%20studio%20A-frame%20villa%20with%20viewing%20deck%20on%20a%20hilltop%20at%20dusk%2C%20warm%20interior%20lights%20glowing%2C%20stylized%20architectural%20render%2C%20golden%20and%20teal%20tones%2C%20high%20detail&width=800&height=600&seq=list-02&orientation=landscape",
  },
  {
    id: 3,
    slug: "havenside",
    name: "Havenside",
    type: "2 BHK Family",
    bedrooms: "2 Beds",
    area: "720 sq ft",
    price: "From ₹98 L",
    roi: "32%",
    style: "Family",
    desc: "A spacious two-bedroom family retreat that rents beautifully while doubling as a personal holiday home.",
    image: "https://readdy.ai/api/search-image?query=Spacious%20two%20bedroom%20wooden%20A-frame%20family%20villa%20with%20large%20windows%20and%20garden%20near%20a%20forest%2C%20soft%20morning%20light%2C%20warm%20cozy%20architectural%20render%2C%20high%20detail&width=800&height=600&seq=list-03&orientation=landscape",
  },
  {
    id: 4,
    slug: "slopeview",
    name: "Slopeview",
    type: "Premium Glass",
    bedrooms: "2 Beds",
    area: "860 sq ft",
    price: "From ₹1.2 Cr",
    roi: "34%",
    style: "Airbnb",
    desc: "Floor-to-ceiling glass and an elevated deck facing the valley — engineered for premium nightly rates.",
    image: "https://readdy.ai/api/search-image?query=Premium%20glass%20walled%20A-frame%20villa%20perched%20on%20a%20sloped%20hillside%20overlooking%20a%20valley%20at%20sunrise%2C%20warm%20light%2C%20luxury%20architectural%20render%2C%20high%20detail&width=800&height=600&seq=list-04&orientation=landscape",
  },
  {
    id: 5,
    slug: "crestline",
    name: "Crestline",
    type: "3 BHK Resort",
    bedrooms: "3 Beds",
    area: "980 sq ft",
    price: "From ₹1.45 Cr",
    roi: "38%",
    style: "Resort",
    desc: "A resort-grade three-bedroom model built for boutique stays and high-occupancy hospitality clusters.",
    image: "https://readdy.ai/api/search-image?query=Large%20three%20bedroom%20A-frame%20resort%20villa%20with%20outdoor%20pool%20and%20lounge%20deck%2C%20lush%20tropical%20setting%2C%20golden%20hour%2C%20luxury%20architectural%20render%2C%20high%20detail&width=800&height=600&seq=list-05&orientation=landscape",
  },
  {
    id: 6,
    slug: "ridgetop",
    name: "Ridgetop",
    type: "Dual Storey",
    bedrooms: "3 Beds",
    area: "1240 sq ft",
    price: "From ₹1.8 Cr",
    roi: "40%",
    style: "Resort",
    desc: "Our flagship two-storey A-frame with a rooftop viewing lounge — built for flagship resort anchors.",
    image: "https://readdy.ai/api/search-image?query=Grand%20two%20storey%20A-frame%20villa%20with%20rooftop%20viewing%20lounge%20and%20terrace%20on%20a%20ridge%20line%20at%20sunset%2C%20warm%20lights%2C%20flagship%20architectural%20render%2C%20high%20detail&width=800&height=600&seq=list-06&orientation=landscape",
  },
];

const FILTERS = ["All", "Airbnb", "Staycation", "Family", "Resort"];

export default function Listings() {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const visible = MODELS.filter((m) => {
    const matchStyle = filter === "All" || m.style === filter;
    const matchQuery =
      !query ||
      m.name.toLowerCase().includes(query.toLowerCase()) ||
      m.type.toLowerCase().includes(query.toLowerCase());
    return matchStyle && matchQuery;
  });

  return (
    <div className="w-full">
      <PageHero
        eyebrow="Villa Models & Gallery"
        title={
          <>
            Modular <span className="italic text-accent-300">A-Frame Villas</span>
          </>
        }
        subtitle="Explore our catalogue of modular A-frame villa models — each engineered for hospitality, comfort and strong rental returns."
      />

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`text-sm font-medium whitespace-nowrap rounded-full px-4 py-2 transition-colors ${
                    filter === f
                      ? "bg-primary-600 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by model or type..."
                className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {visible.map((m) => (
              <article
                key={m.id}
                className="group rounded-2xl bg-background-100 border border-background-200 overflow-hidden hover:border-primary-300 transition-colors"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={m.image}
                    alt={`${m.name} ${m.type} A-frame villa`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 rounded-full bg-primary-600 text-background-50 text-[11px] font-semibold px-3 py-1">
                    {m.style}
                  </span>
                  <span className="absolute top-3 right-3 rounded-full bg-background-50/90 backdrop-blur text-foreground-950 text-xs font-bold px-3 py-1">
                    {m.price}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg text-foreground-950">{m.name}</h3>
                    <span className="text-[11px] uppercase tracking-wide text-primary-600 font-semibold">{m.type}</span>
                  </div>
                  <p className="flex items-center gap-1.5 text-sm text-foreground-600 mt-1">
                    <i className="ri-map-pin-2-line text-primary-600"></i>
                    {m.bedrooms} · {m.area} built-up
                  </p>
                  <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{m.desc}</p>
                  <div className="mt-4 flex items-center justify-between rounded-xl bg-background-50 border border-background-200 px-4 py-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-foreground-600">Est. ROI</p>
                      <p className="text-sm font-semibold text-foreground-950">{m.roi} annually</p>
                    </div>
                    <a
                      href={m.slug ? `/villa/${m.slug}` : "/contact"}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap text-primary-700 hover:text-primary-800"
                    >
                      Explore <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {visible.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
              <p className="text-foreground-600 mt-4">No villa models match your search.</p>
            </div>
          )}

          <div className="mt-12 rounded-2xl bg-primary-100 border border-primary-200 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5">
            <div>
              <h4 className="font-heading text-xl text-foreground-950">Want to see them on-site?</h4>
              <p className="text-sm text-foreground-700 mt-1">
                Book a site visit or download our 2026 investment guide with full specs and returns.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="/projects"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-primary-600 text-primary-700 px-6 py-3 hover:bg-primary-600 hover:text-background-50 transition-colors"
              >
                View Projects <i className="ri-map-2-line"></i>
              </a>
              <a
                href="/investment-guide"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700 transition-colors"
              >
                Download Guide <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}